import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import {
	NativeBiometric,
	BiometryType,
	BiometricAuthError,
} from "@capgo/capacitor-native-biometric";
import { environment } from "src/environments/environment";

/**
 * Códigos de error normalizados que expone este servicio hacia la UI.
 * Permiten reaccionar de forma específica a cada situación sin acoplar la UI
 * a los códigos nativos (que difieren entre Android e iOS).
 */
export type BiometricErrorReason =
	| "unavailable" // El dispositivo no soporta biometría o no hay huellas/rostro registrados
	| "no-credentials" // No hay credenciales biométricas guardadas
	| "biometry-changed" // El método biométrico cambió respecto al momento de habilitación
	| "user-cancel" // El usuario canceló el prompt
	| "lockout" // Demasiados intentos fallidos: biometría bloqueada temporalmente
	| "auth-failed" // La verificación biométrica falló
	| "unknown";

/** Tipo de ícono a mostrar en el prompt/modal de biometría. */
export type BiometricIconKind = "face" | "fingerprint" | "iris";

export class BiometricError extends Error {
	constructor(
		public reason: BiometricErrorReason,
		public nativeCode?: number,
		message?: string
	) {
		super(message || reason);
		this.name = "BiometricError";
	}
}

/**
 * Entrada de credencial de UNA marca dentro del mapa multimarca.
 *
 * Todas las marcas se guardan juntas en UN ÚNICO slot de credencial nativa
 * (ver `BiometricAuthService`), como un mapa `{ brandKey: BrandEntry }`
 * serializado en el campo `password` de setCredentials, que el plugin persiste
 * cifrado por hardware (iOS Keychain / Android Keystore + EncryptedSharedPreferences).
 *
 * Motivo del slot único: el plugin `deleteCredentials` en Android hace un
 * `editor.clear()` que borra TODAS las credenciales del almacén, no solo la del
 * `server` indicado. Por eso NO podemos usar un `server` distinto por marca para
 * aislarlas (borrar una borraría todas). En su lugar mantenemos un solo slot y
 * gestionamos el mapa por marca desde JS: agregar/quitar una marca = reescribir
 * el mapa (silencioso, sin prompt biométrico).
 */
interface BrandEntry {
	username: string; // identificador de login de esa marca
	p: string; // contraseña
	bt: number; // BiometryType registrado al habilitar (para detectar cambios)
	at: number; // timestamp de habilitación
	uid?: string; // id del usuario dueño (para sincronizar cambios de contraseña)
}

/** Mapa de marcas: brandKey -> credencial. */
type BrandCredentialMap = Record<string, BrandEntry>;

/** Clave del mapa para el caso de marca única (brandKey nulo). */
const SINGLE_BRAND_KEY = "__single__";

/** Username placeholder del slot nativo único (el username real vive en cada BrandEntry). */
const MAP_SLOT_USERNAME = "cursalab-biometric";

@Injectable({ providedIn: "root" })
export class BiometricAuthService {
	/**
	 * `server` del ÚNICO slot de credencial nativa donde se guarda el mapa de todas
	 * las marcas. Es fijo: no usamos un server por marca porque el `deleteCredentials`
	 * del plugin en Android borra TODO el almacén (ver `BrandEntry`).
	 */
	private readonly baseServer = environment.APP_ID_PLAYSTORE || "io.cursalab.app";

	/** Normaliza el brandKey a la clave del mapa (nulo/vacío => marca única). */
	private mapKeyFor(brandKey?: string): string {
		const key = (brandKey || "").trim();
		return key || SINGLE_BRAND_KEY;
	}

	/**
	 * Lee el mapa de credenciales del slot nativo. Silencioso (getCredentials NO
	 * dispara prompt biométrico en iOS ni Android). Tolerante a errores => {}.
	 *
	 * Migra el formato antiguo (envelope único `{v,p,bt,...}`) al mapa bajo la
	 * clave de marca única, para no perder credenciales enroladas con la versión
	 * previa del servicio.
	 */
	private async readMap(): Promise<BrandCredentialMap> {
		if (!this.isNative) return {};
		let stored;
		try {
			stored = await NativeBiometric.getCredentials({ server: this.baseServer });
		} catch {
			return {};
		}
		if (!stored?.password) return {};
		try {
			const parsed = JSON.parse(stored.password);
			if (!parsed || typeof parsed !== "object") return {};
			// Formato antiguo (envelope único): migramos a mapa["__single__"].
			if (typeof parsed.p === "string" && typeof parsed.bt === "number") {
				return {
					[SINGLE_BRAND_KEY]: {
						username: stored.username,
						p: parsed.p,
						bt: parsed.bt,
						at: parsed.at,
						uid: parsed.uid,
					},
				};
			}
			return parsed as BrandCredentialMap;
		} catch {
			return {};
		}
	}

	/**
	 * Persiste el mapa en el slot nativo. Si queda vacío, elimina el slot completo.
	 * Silencioso (setCredentials no dispara prompt).
	 */
	private async writeMap(map: BrandCredentialMap): Promise<void> {
		if (!this.isNative) return;
		if (Object.keys(map).length === 0) {
			try {
				await NativeBiometric.deleteCredentials({ server: this.baseServer });
			} catch {
				// Si no existía, no es un error.
			}
			return;
		}
		await NativeBiometric.setCredentials({
			username: MAP_SLOT_USERNAME,
			password: JSON.stringify(map),
			server: this.baseServer,
		});
	}

	/**
	 * Identificador de login (lo que el usuario tecleó en el campo "usuario")
	 * de la sesión actual. Se guarda SOLO en memoria (nunca persistido) para
	 * permitir re-validar la contraseña al activar la biometría desde el perfil.
	 * Se pierde al cerrar la app: en ese caso el perfil cae a un fallback.
	 */
	private _sessionUsername: string | null = null;

	setSessionUsername(username: string | null) {
		this._sessionUsername = username || null;
	}

	get sessionUsername(): string | null {
		return this._sessionUsername;
	}

	private _skipNextAutoLogin = false;

	/** Marca que el próximo ingreso a Login no debe auto-disparar biometría (p.ej. tras logout explícito). */
	markSkipNextAutoLogin() {
		this._skipNextAutoLogin = true;
	}

	/** Consume (lee y resetea) la bandera de supresión de auto-login. */
	consumeSkipNextAutoLogin(): boolean {
		const skip = this._skipNextAutoLogin;
		this._skipNextAutoLogin = false;
		return skip;
	}

	/** ¿Estamos en un dispositivo nativo (no web/PWA)? */
	private get isNative(): boolean {
		return Capacitor.isNativePlatform();
	}

	/**
	 * Disponibilidad real del hardware biométrico + tipo.
	 * No lanza: ante cualquier problema devuelve "no disponible".
	 */
	async getAvailability(): Promise<{
		available: boolean;
		biometryType: BiometryType;
		errorCode?: BiometricAuthError;
	}> {
		if (!this.isNative) {
			return { available: false, biometryType: BiometryType.NONE };
		}
		try {
			// useFallback: false => exigimos biometría real, no el PIN del dispositivo.
			const result = await NativeBiometric.isAvailable({ useFallback: false });
      console.log("** getAvailability:", result)
      console.log("** getAvailability2:", result?.biometryType)
			return {
				available: !!result.isAvailable,
				biometryType: result.biometryType,
				errorCode: result.errorCode,
			};
		} catch {
			return { available: false, biometryType: BiometryType.NONE };
		}
	}

	/**
	 * Etiqueta legible según el método disponible en el dispositivo.
	 * Ej: "Face ID", "Touch ID", "huella".
	 */
	async getActionLabel(lang: string = "es"): Promise<string> {
		const { biometryType } = await this.getAvailability();
		return this.resolvePromptInfo(biometryType, lang).label;
	}

	/**
	 * Metadatos para construir el prompt/modal de biometría: etiqueta legible y
	 * tipo de ícono ("face" | "fingerprint" | "iris"), resueltos según el
	 * BiometryType específico del dispositivo.
	 *
	 * Para BiometryType.MULTIPLE (Android con varios métodos) se decide por
	 * plataforma: cara en iOS, huella en Android.
	 */
	async getPromptInfo(
		lang: string = "es"
	): Promise<{ label: string; iconKind: BiometricIconKind }> {
		const { biometryType } = await this.getAvailability();
		return this.resolvePromptInfo(biometryType, lang);
	}

	private resolvePromptInfo(
		biometryType: BiometryType,
		lang: string
	): { label: string; iconKind: BiometricIconKind } {
		const es = lang !== "en";
		const isIos = Capacitor.getPlatform() === "ios";

		switch (biometryType) {
			case BiometryType.FACE_ID:
				return { label: "Face ID", iconKind: "face" };
			case BiometryType.TOUCH_ID:
				return { label: "Touch ID", iconKind: "fingerprint" };
			case BiometryType.FINGERPRINT:
				return { label: es ? "huella" : "fingerprint", iconKind: "fingerprint" };
			case BiometryType.FACE_AUTHENTICATION:
				return {
					label: es ? "reconocimiento facial" : "face recognition",
					iconKind: "face",
				};
			case BiometryType.IRIS_AUTHENTICATION:
				return { label: es ? "iris" : "iris", iconKind: "iris" };
			case BiometryType.MULTIPLE:
			default:
				// Varios métodos (o desconocido): cara en iOS, huella en Android.
				return isIos
					? { label: "Face ID", iconKind: "face" }
					: { label: es ? "huella" : "fingerprint", iconKind: "fingerprint" };
		}
	}

	/**
	 * ¿Existe una credencial biométrica guardada para esta marca?
	 * Lee el mapa (silencioso, sin prompt) y comprueba la clave de la marca.
	 */
	async isEnabled(brandKey?: string): Promise<boolean> {
		if (!this.isNative) return false;
		const map = await this.readMap();
		return !!map[this.mapKeyFor(brandKey)];
	}

	/**
	 * ¿Hay biometría guardada para ESTE usuario en esta marca?
	 * Necesario para decidir si re-ofrecer el enrolamiento. Silencioso.
	 */
	async isEnabledFor(username: string, brandKey?: string): Promise<boolean> {
		if (!this.isNative || !username) return false;
		const entry = (await this.readMap())[this.mapKeyFor(brandKey)];
		return (
			!!entry?.username &&
			entry.username.toLowerCase() === username.toLowerCase()
		);
	}

	/**
	 * Habilita el acceso biométrico para un usuario.
	 *
	 * Flujo:
	 *  1. Verifica que el hardware esté disponible.
	 *  2. Pide verificación biométrica nativa (confirma que quien habilita es
	 *     el dueño legítimo del dispositivo).
	 *  3. Agrega/actualiza SOLO la entrada de ESTA marca en el mapa (las demás
	 *     marcas se conservan intactas).
	 *
	 * En multimarca, `brandKey` aísla la credencial por marca: enrolar la marca B
	 * NO borra la credencial de la marca A.
	 */
	async enable(
		username: string,
		password: string,
		userId: string,
		lang: string = "es",
		brandKey?: string
	): Promise<void> {
		const { available, biometryType } = await this.getAvailability();
		if (!available) throw new BiometricError("unavailable");

		await this.verifyIdentity(lang);

		const map = await this.readMap();
		map[this.mapKeyFor(brandKey)] = {
			username,
			p: password,
			bt: biometryType,
			at: Date.now(),
			uid: userId,
		};
		await this.writeMap(map);
	}

	/**
	 * Recupera las credenciales tras una verificación biométrica exitosa.
	 *
	 * Maneja los casos de borde:
	 *  - Hardware no disponible / huellas eliminadas / permiso revocado => limpia y lanza.
	 *  - El método biométrico cambió (p.ej. de huella a Face) => limpia y lanza "biometry-changed".
	 *  - Cancelación / fallo / lockout => lanza el motivo correspondiente.
	 */
	async retrieveCredentials(
		lang: string = "es",
		brandKey?: string
	): Promise<{ username: string; password: string }> {
		const { available, biometryType } = await this.getAvailability();
		if (!available) {
			// Biometría removida o permiso revocado: NINGUNA credencial es utilizable
			// de forma segura => se eliminan todas.
			await this.clearAll();
			throw new BiometricError("unavailable");
		}

		// Dispara el prompt nativo (huella / Face ID / Touch ID). Lanza si falla.
		await this.verifyIdentity(lang);

		const entry = (await this.readMap())[this.mapKeyFor(brandKey)];
		if (!entry) throw new BiometricError("no-credentials");

		// Defensa adicional: si el TIPO de biometría cambió desde que se habilitó
		// (p.ej. el usuario pasó de huella a reconocimiento facial), invalidamos
		// la credencial de esta marca y forzamos login con contraseña.
		if (entry.bt !== biometryType) {
			await this.clear(brandKey);
			throw new BiometricError("biometry-changed");
		}

		return { username: entry.username, password: entry.p };
	}

	/**
	 * Elimina la credencial biométrica de UNA marca (o la única, en modo marca
	 * única) reescribiendo el mapa sin esa entrada. Las demás marcas se conservan.
	 */
	async clear(brandKey?: string): Promise<void> {
		if (!this.isNative) return;
		const map = await this.readMap();
		const key = this.mapKeyFor(brandKey);
		if (map[key]) {
			delete map[key];
			await this.writeMap(map); // si queda vacío, writeMap elimina el slot
		}
	}

	/**
	 * Elimina TODAS las credenciales biométricas del dispositivo (el slot único con
	 * el mapa completo). Se usa cuando el hardware biométrico deja de estar
	 * disponible: ninguna credencial vuelve a ser utilizable de forma segura.
	 */
	async clearAll(): Promise<void> {
		if (!this.isNative) return;
		try {
			await NativeBiometric.deleteCredentials({ server: this.baseServer });
		} catch {
			// Si no existía, no es un error.
		}
	}

	/**
	 * Sincroniza la contraseña guardada cuando el usuario cambia su contraseña.
	 *
	 * Solo actualiza si la credencial biométrica pertenece a ESE mismo usuario
	 * (match por `uid`). Si pertenece a otro usuario, no hay credencial, o la
	 * credencial es antigua (sin `uid`), no hace nada y retorna false.
	 *
	 * La actualización es silenciosa (sin prompt biométrico): el usuario ya está
	 * autenticado (sesión activa o token de reset validado), por lo que es seguro.
	 *
	 * @returns true si actualizó la credencial; false en cualquier otro caso.
	 */
	async updatePasswordForUser(userId: string, newPassword: string, brandKey?: string): Promise<boolean> {
		if (!this.isNative || !userId || !newPassword) return false;
		try {
			const map = await this.readMap();
			const key = this.mapKeyFor(brandKey);
			const entry = map[key];
			// Solo actualizamos si la credencial de esta marca es del mismo usuario.
			if (!entry || !entry.uid || entry.uid != userId) return false;

			entry.p = newPassword;
			await this.writeMap(map);
			return true;
		} catch {
			return false;
		}
	}

	// ---------------------------------------------------------------------------
	// Internos
	// ---------------------------------------------------------------------------

	private async verifyIdentity(lang: string): Promise<void> {
		const es = lang !== "en";
		try {
			await NativeBiometric.verifyIdentity({
				reason: es
					? "Confirma tu identidad para continuar"
					: "Confirm your identity to continue",
				title: es ? "Verificación biométrica" : "Biometric verification",
				subtitle: es ? "Usa tu biometría registrada" : "Use your registered biometrics",
				useFallback: false, // Sin PIN del dispositivo: solo biometría.
				maxAttempts: 3,
			});
		} catch (err: any) {
			throw this.mapNativeError(err);
		}
	}

	/**
	 * Normaliza los errores nativos (Android/iOS) a un BiometricError tipado.
	 * Los códigos provienen del enum BiometricAuthError del plugin (unificado
	 * entre plataformas).
	 */
	private mapNativeError(err: any): BiometricError {
		const code = Number(err?.code ?? err?.errorCode ?? -1);
		const msg = err?.message;

		switch (code) {
			case BiometricAuthError.USER_CANCEL: // 16
			case BiometricAuthError.SYSTEM_CANCEL: // 15
			case BiometricAuthError.APP_CANCEL: // 11
			case BiometricAuthError.USER_FALLBACK: // 17
				return new BiometricError("user-cancel", code, msg);

			case BiometricAuthError.USER_LOCKOUT: // 2
			case BiometricAuthError.USER_TEMPORARY_LOCKOUT: // 4
				return new BiometricError("lockout", code, msg);

			case BiometricAuthError.BIOMETRICS_UNAVAILABLE: // 1
			case BiometricAuthError.BIOMETRICS_NOT_ENROLLED: // 3
			case BiometricAuthError.PASSCODE_NOT_SET: // 14
				return new BiometricError("unavailable", code, msg);

			case BiometricAuthError.AUTHENTICATION_FAILED: // 10
				return new BiometricError("auth-failed", code, msg);

			default:
				return new BiometricError("unknown", code, msg);
		}
	}
}
